import type {
  EditableResumeField,
  OptimizationSuggestion,
} from "@/client/pages/dashboard/ai-optimization/review";

import { normalizeForComparison } from "./verify-suggestions";

/**
 * The résumé's own wording, captured before the optimization run touches anything.
 *
 * WHY `previousText` IS NOT ENOUGH: it is re-baselined on every run. The intake builds the
 * prompt from `buildEditableResumeFields(baseData)` against the résumé AS IT STANDS, so if a
 * previous run's rewrite was accepted and saved, the model is handed that rewrite as the
 * original and returns it as `previousText`. Reject then "restores" a rewrite, and the
 * student's own words are gone for good — each run rewrites the previous rewrite.
 *
 * Observed: a résumé seeded with "Detail-oriented QA Engineer with 3 years of experience in
 * manual testing…" was saved as "Detail-oriented Software Developer with 3 years of
 * experience in software testing and development…", and no reject could get the first back.
 *
 * So the values are snapshotted at run start and held for the life of the session. Reject
 * restores from here.
 *
 * SCOPE, stated honestly: this is the content at the start of THIS run, which is the
 * correct meaning of "undo what this run did". It cannot recover text a previous run already
 * saved over — that wording is not stored anywhere. It stops the loss from continuing.
 *
 * Docs: lms-backend/docs/plans/2026-09-23-coach-resume-improvement.md
 */

export type OriginalFieldValues = Record<string, string>;

/**
 * Snapshot every field the model is allowed to touch, keyed by the same `fieldPath` the
 * suggestions carry.
 *
 * @param editableFields - output of buildEditableResumeFields, read BEFORE any accept
 */
export const captureOriginalValues = (
  editableFields: EditableResumeField[],
): OriginalFieldValues => {
  const originals: OriginalFieldValues = {};

  for (const field of editableFields) {
    if (!field?.fieldPath) continue;
    originals[field.fieldPath] = String(field.value ?? "");
  }

  return originals;
};

/**
 * What should reject put back for this suggestion?
 *
 * Prefers the run-start snapshot and falls back to `previousText`, so a suggestion for a
 * field that somehow was not snapshotted still reverts to something rather than silently
 * doing nothing.
 */
export const resolveRevertText = (
  suggestion: OptimizationSuggestion,
  originals: OriginalFieldValues | null | undefined,
): string => {
  const snapshot = originals?.[suggestion.fieldPath];

  return typeof snapshot === "string" ? snapshot : suggestion.previousText;
};

/**
 * What the struck-out "before" half of a suggestion card should show.
 *
 * THE BUG THIS FIXES. The card used to read this straight off the résumé at the
 * suggestion's `fieldPath`. Accept writes the suggested text to that same path, so the
 * instant a summary was accepted the red "before" line became the green "after" text and
 * the diff showed a change from the new wording to the new wording.
 *
 * Two different questions, depending on whether the card has been accepted:
 *   - not accepted → "what would this overwrite?" That is the live résumé value, so a
 *     manual edit made after the plan was generated is shown as what is at stake rather
 *     than being hidden behind a stale snapshot.
 *   - accepted → "what did this replace?" That is the run-start snapshot, and it is
 *     resolved through `resolveRevertText` on purpose: it is exactly the text Reject will
 *     put back, so what the student reads as the original and what they get if they undo
 *     cannot drift apart.
 *
 * @param liveValue - current résumé value at `suggestion.fieldPath`, if any
 */
export const resolveDiffBeforeText = ({
  suggestion,
  isAccepted,
  liveValue,
  originals,
}: {
  suggestion: OptimizationSuggestion;
  isAccepted: boolean;
  liveValue?: string;
  originals: OriginalFieldValues | null | undefined;
}): string => {
  if (isAccepted) return resolveRevertText(suggestion, originals);

  return liveValue ?? suggestion.previousText;
};

/**
 * Is this suggestion asking for a change that is already in the résumé?
 *
 * Re-running the optimizer on an already-optimized résumé produces these: the model returns
 * the text that is already stored. Showing them is worse than useless — the student accepts,
 * nothing changes, and the score does not move, which reads as the feature being broken.
 * (That is exactly what "I accept a change in skills and the score doesn't increase" was.)
 */
export const isNoOpSuggestion = (
  suggestion: OptimizationSuggestion,
  originals: OriginalFieldValues | null | undefined,
): boolean => {
  const current = originals?.[suggestion.fieldPath];
  if (typeof current !== "string") return false;

  return normalizeForComparison(current) === normalizeForComparison(suggestion.suggestedText);
};

/**
 * Fraction of the replacement's words that must be NEW for it to count as substantive.
 *
 * Novelty rather than overlap, deliberately: an overlap ratio measured against the longer
 * text can be lowered just by padding a reword with filler, so the padding itself would make
 * a cosmetic change look substantial. "How much of this text is actually new?" cannot be
 * gamed that way.
 *
 * Measured on real output rather than guessed. Novelty ratios for one summary:
 *   synonym shuffle that reached a student  0.20
 *   the same, padded with filler            0.25
 *   genuine rewrite (new capability language) 0.89
 *
 * Cosmetic and substantive are separated by a wide margin, so 0.35 sits comfortably between
 * them and the exact value is not load-bearing.
 */
const SUBSTANTIVE_NOVELTY = 0.35;

/**
 * Significant words, punctuation stripped.
 *
 * Stripping matters more than it looks: without it "testing" and "testing." are different
 * tokens, which inflated the apparent difference between two texts enough that a pure synonym
 * swap measured as substantially rewritten and slipped through.
 */
const wordsOf = (value: string) =>
  new Set(
    normalizeForComparison(value)
      .replace(/[^a-z0-9\s]/g, " ")
      .split(/\s+/)
      .filter((word) => word.length > 2),
  );

/**
 * Is this a rewrite that adds nothing real — either a reword, or bare keyword stuffing?
 *
 * Two failures, both of which reached a student.
 *
 * 1. REWORD. Two rewrites of one summary differing only as "detail-oriented"/"results-driven"
 *    and "comprehensive"/"detailed" scored identically, because nothing the scorer reads had
 *    changed. Accepting one moved the number by zero.
 *
 * 2. KEYWORD STUFFING, and this one is worse than a wasted click. Asked to close the gaps
 *    "Product Management" and "Stakeholder Management" on a QA résumé, the model returned a
 *    summary with the words inserted into an existing list:
 *
 *      "Skilled in product management, stakeholder management, designing and executing
 *       detailed test cases, tracking defects…"
 *
 *    No work described, no evidence, just the terms. That is a FALSE CLAIM on a student's
 *    résumé — they would have to defend it in an interview — and it is precisely what the
 *    prompt forbids. An earlier version of this function exempted anything that added a target
 *    keyword from the substance check, which waved it straight through. That exemption was the
 *    bug: "mentions the skill" is not the same as "evidences the skill".
 *
 * So novelty is measured with the target keywords' own words EXCLUDED. An edit whose only new
 * content is the keywords scores ~0 and is rejected; an edit that names a capability AND
 * describes the work behind it keeps plenty of novel words and passes. One rule, both failures.
 *
 * @param targetKeywords - the LMS's missingSkills; what skillOverlap is penalising
 */
export const isLowValueRewrite = (
  suggestion: OptimizationSuggestion,
  originals: OriginalFieldValues | null | undefined,
  targetKeywords: string[] = [],
): boolean => {
  const current = originals?.[suggestion.fieldPath];
  if (typeof current !== "string" || !current.trim()) return false;

  const beforeWords = wordsOf(current);
  const afterWords = wordsOf(suggestion.suggestedText);
  if (beforeWords.size === 0 || afterWords.size === 0) return false;

  // The keywords themselves cannot count as substance, or naming a skill would be enough to
  // claim it.
  const keywordWords = new Set<string>();
  for (const keyword of targetKeywords) {
    for (const word of wordsOf(keyword)) keywordWords.add(word);
  }

  let novel = 0;
  let considered = 0;
  for (const word of afterWords) {
    if (keywordWords.has(word)) continue;
    considered += 1;
    if (!beforeWords.has(word)) novel += 1;
  }

  if (considered === 0) return true;

  return novel / considered < SUBSTANTIVE_NOVELTY;
};

/**
 * Drop suggestions that would change nothing the scorer can see.
 *
 * Returns both halves so the caller can log what was dropped — a run that is entirely
 * no-ops is a meaningful signal (the résumé is already optimized for this job), not an
 * error to swallow.
 *
 * @param targetKeywords - the LMS's missingSkills, so gap-closing edits are never dropped
 */
export const dropNoOpSuggestions = (
  suggestions: OptimizationSuggestion[],
  originals: OriginalFieldValues | null | undefined,
  targetKeywords: string[] = [],
) => {
  const actionable: OptimizationSuggestion[] = [];
  const noOps: OptimizationSuggestion[] = [];

  for (const suggestion of suggestions) {
    if (
      isNoOpSuggestion(suggestion, originals) ||
      isLowValueRewrite(suggestion, originals, targetKeywords)
    ) {
      noOps.push(suggestion);
    } else {
      actionable.push(suggestion);
    }
  }

  return { actionable, noOps };
};
